import { createSlice } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../store';
import axios from '../../resources/axios';
import { Admin, AdminInfo, CreateAdminBody } from '../../resources/adminsTypes';
import { getLocalStorage, setLocalStorage } from '../../resources/functions';
import { STORE_CURRENT_CITY } from '../../resources/storageVariables';
import { signOut, updateType } from './authSlice';

interface AdminsState {
  items: Admin[];
  info?: AdminInfo;
  // Логін тримаємо окремо від info: суперадміну info не завантажуємо, бо
  // від нього залежать права й список міст, а логін у меню потрібен усім.
  login?: string;
  currentCity?: string;
  isGoogleApiInit: boolean;
}

const initialState: AdminsState = {
  items: [],
  isGoogleApiInit: false,
  currentCity: getLocalStorage(STORE_CURRENT_CITY) || undefined
};

const adminsSlice = createSlice({
  name: 'admins',
  initialState,
  reducers: {
    getAdmins: (state, action) => {
      state.items = action.payload.admins || [];
    },
    getAdminInfo: (state, action) => {
      console.log('🔍 adminsSlice getAdminInfo - payload:', action.payload);
      console.log('🔍 admin object:', action.payload.admin);
      console.log('🔍 admin.cities:', action.payload.admin.cities);
      console.log('🔍 admin.workshops:', action.payload.admin.workshops);
      console.log('🔍 admin.type:', action.payload.admin.type);
      
      // Use stored currentCity if exists and is valid for this admin, otherwise use first city
      const storedCity = state.currentCity;
      const adminCities = action.payload.admin.cities || [];
      const isStoredCityValid = storedCity && adminCities.some((city: { _id: string }) => city._id === storedCity);
      
      // Determine currentCity: try stored, then first city, then first workshop city
      let currentCity = isStoredCityValid ? storedCity : adminCities[0]?._id;
      console.log('🔍 currentCity from cities:', currentCity);
      
      // For Masters/Workers who might not have cities array but have workshops
      if (!currentCity && action.payload.admin.workshops?.length > 0) {
        const workshop = action.payload.admin.workshops[0];
        console.log('🔍 First workshop:', workshop);
        console.log('🔍 workshop.city:', workshop.city);
        console.log('🔍 workshop.cityId:', workshop.cityId);
        currentCity = workshop.city?._id || workshop.cityId;
        console.log('🔍 currentCity from workshop:', currentCity);
      }
      
      state.info = action.payload.admin;
      state.login = action.payload.admin.login;
      state.currentCity = currentCity;
      
      console.log('✅ Final currentCity set in state:', currentCity);
      
      // Only set city header if currentCity exists
      if (currentCity) {
        axios.defaults.headers.common['city'] = currentCity;
        setLocalStorage(STORE_CURRENT_CITY, currentCity);
        console.log('✅ City header set:', currentCity);
      } else {
        console.warn('⚠️ No currentCity - city header NOT set');
      }
    },
    setAdminLogin: (state, action) => {
      state.login = action.payload;
    },
    setCurrentCity: (state, action) => {
      const currentCity = action.payload;
      state.currentCity = currentCity;
      axios.defaults.headers.common['city'] = currentCity;
      setLocalStorage(STORE_CURRENT_CITY, currentCity);
    },
    initGoogleApi: state => {
      state.isGoogleApiInit = true;
    }
  }
});

export const {
  getAdmins,
  getAdminInfo,
  setAdminLogin,
  setCurrentCity,
  initGoogleApi
} = adminsSlice.actions;

// Скрипт вантажиться з loading=async: у цьому режимі неймспейс google.maps
// зʼявляється РАНІШЕ за конструктори, тому наявність google.maps ще нічого не
// означає. Якщо повірити їй і змонтувати <GoogleMap>, той у componentDidMount
// викличе new google.maps.Map(...) і впаде з "google.maps.Map is not a
// constructor", а React 17 знесе все дерево — це і є білий екран до F5.
const isMapsReady = () => Boolean((window as any).google?.maps?.Map);

// Штатний спосіб дочекатись бібліотеки в режимі loading=async.
const importMapsLibrary = async () => {
  const maps = (window as any).google?.maps;

  if (maps?.importLibrary) {
    try {
      await maps.importLibrary('maps');
    } catch {
      // Не вийшло — далі спрацює полінг або таймаут.
    }
  }

  return isMapsReady();
};

export const initGoogleApiAsync = (): AppThunk<Promise<void>> => async dispatch => {
  const apiUrl =
    'https://maps.googleapis.com/maps/api/js?key=AIzaSyBu5VqKowtFVHsHfMDxdvvspcbjzztTvSA&libraries=places,visualization,geometry&loading=async';

  if (isMapsReady()) {
    dispatch(initGoogleApi());
    return;
  }

  const oldScripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
  oldScripts.forEach(script => {
    if (!script.getAttribute('src')?.includes('geometry')) {
      script.remove();
    }
  });

  const waitForGoogle = (script: HTMLScriptElement, resolve: () => void) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearInterval(poll);
      clearTimeout(timeout);
      // Прапорець піднімаємо лише коли конструктор реально є: сторінки з
      // картою орієнтуються на нього, щоб не рендерити <GoogleMap> дарма.
      if (isMapsReady()) dispatch(initGoogleApi());
      resolve();
    };
    // load спрацьовує на самому бутстрап-скрипті, коли Map ще немає, тому
    // тут довантажуємо бібліотеку і завершуємо лише після цього.
    script.addEventListener('load', async () => {
      if (await importMapsLibrary()) finish();
    });
    script.addEventListener('error', finish);
    const poll = setInterval(() => {
      if (isMapsReady()) finish();
    }, 100);
    const timeout = setTimeout(finish, 10000);
  };

  const existing = document.querySelector(`script[src="${apiUrl}"]`) as HTMLScriptElement | null;
  if (existing) {
    return new Promise<void>(resolve => {
      if (isMapsReady()) {
        dispatch(initGoogleApi());
        resolve();
        return;
      }
      waitForGoogle(existing, resolve);
    });
  }

  return new Promise<void>(resolve => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = apiUrl;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
    waitForGoogle(script, resolve);
  });
};

export const getAdminsAsync = (cityIds = ''): AppThunk => async dispatch => {
  const response = await axios.get(`/admins${cityIds ? `?cities=${cityIds}` : ''}`);
  dispatch(getAdmins(response.data));
};

export const getAdminAsync = (adminId: string): AppThunk => async () => {
  const response = await axios.get(`/admins/${adminId}`);
  return response.data;
};

export const createAdminAsync = (
  body: CreateAdminBody
): AppThunk => async () => {
  const response = await axios.post('/admins', body);
  return response;
};

export const updateAdminAsync = (
  adminId: string,
  body: CreateAdminBody
): AppThunk => async () => {
  const response = await axios.put(`/admins/${adminId}`, body);
  return response;
};

export const toggleAdminDisabledAsync = (
  adminId: string,
  disabled: boolean
): AppThunk => async () => {
  await axios.put(`/admins/${adminId}`, { disabled });
};

export const deleteAdminAsync = (adminId: string): AppThunk => async () => {
  await axios.delete(`/admins/${adminId}`);
};

export const getAdminInfoAsync = (): AppThunk => async dispatch => {
  try {
    const response = await axios.get('/admins/info');
    dispatch(getAdminInfo(response.data));
    if (response.data?.admin?.type) {
      dispatch(updateType(response.data.admin.type));
    }
  } catch (e: any) {
    if (e.response?.status === 401) {
      dispatch(signOut());
    }
  }
};

export const getAdminLoginAsync = (): AppThunk => async dispatch => {
  try {
    const response = await axios.get('/admins/info');
    dispatch(setAdminLogin(response.data?.admin?.login));
  } catch {
    // Логін у меню лише довідковий — без нього інтерфейс працює як раніше.
  }
};

export const selectAdmins = (state: RootState) => state.admins;

export default adminsSlice.reducer;
